import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApp } from '../../context/AppContext.jsx';
import { useProduct } from '../../context/ProductContext.jsx';
import { useSubscription } from '../../context/SubscriptionContext.jsx';

import { startConversation } from '../../api/conversation.js';
import { setLike, sharePost } from '../../api/social.js';

import { sendGTMEvent } from '../../utils/analytics.js';
import { formatBotUrl } from '../../utils/common.js';
import { GA4_KEY, ROUTES } from '../../utils/constants.js';

import CachedImage from '../../components/CachedImage/CachedImage.jsx';
import CommentForm from '../../components/CommentForm/CommentForm.jsx';
import Comments from '../../components/Comments/Comments.jsx';
import PDFViewer from '../../components/PDFViewer/PDFViewer.jsx';

import { colors } from '../../styles/colors.js';
import styles from './Product.module.css';

const Product = () => {
  const navigate = useNavigate();
  const { telegram, user, appDescription } = useApp();

  const { mini_app_url, bot_url, subscription_button } = appDescription;

  const { product, productDetails, setProductsDetails } = useProduct();

  const { subscriptionInfo } = useSubscription();
  const {
    user_data: { premium },
  } = subscriptionInfo;

  const [showComments, setShowComments] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [sharesCount, setSharesCount] = useState(0);
  const [showFullScreenPDF, setShowFullScreenPDF] = useState(false);

  useEffect(() => {
    if (productDetails) {
      setIsLiked(productDetails.is_liked || false);
      setLikesCount(productDetails?.likes?.length || 0);
      setSharesCount(productDetails?.shares?.length || 0);
    }
  }, [productDetails]);

  useEffect(() => {
    telegram.BackButton.show();
    telegram.setHeaderColor(colors.background.main);
    telegram.BackButton.onClick(() => {
      navigate(ROUTES.home);
      telegram.HapticFeedback.impactOccurred('medium');
    });

    return () => {
      telegram.setHeaderColor(colors.background.backend);
      telegram.BackButton.onClick(() => {});
      telegram.BackButton.hide();
    };
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    sendGTMEvent(GA4_KEY.navigate(window.location.href));
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleBuy = () => {
    telegram.HapticFeedback.impactOccurred('medium');
    if ('price' in product) {
      const tg = window.Telegram?.WebApp;
      const isTdesktop = tg.platform === 'tdesktop';

      window.Telegram.WebApp.setHeaderColor('#1D2733');
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      startConversation(product.bot_text);
      if (!tg?.initDataUnsafe?.query_id) {
        tg.openTelegramLink(formatBotUrl(bot_url));
      }
      document.querySelector('.App').classList.add('fadeOut');
      sendGTMEvent(GA4_KEY.buy_product(product.title, product.price, `Заказать ${product.price}`));
      if (isTdesktop) {
        setTimeout(() => {
          tg.close();
        }, 350);
      } else {
        tg.close();
      }
    } else {
      if (product.free) {
        setShowFullScreenPDF(true);
        sendGTMEvent(GA4_KEY.open_guide(product.title));
      } else {
        if (premium) {
          setShowFullScreenPDF(true);
          sendGTMEvent(GA4_KEY.open_guide(product.title));
        } else {
          navigate(ROUTES.subscription, {
            state: { from: ROUTES.product(product.id) },
          });
          sendGTMEvent(GA4_KEY.open_guide(product.title));
        }
      }
    }
  };

  const handleSetLike = async id => {
    telegram.HapticFeedback.impactOccurred(isLiked ? 'light' : 'medium');

    const newIsLiked = !isLiked;

    setProductsDetails(prevDetails => {
      if (!Array.isArray(prevDetails)) return prevDetails;

      const productExists = prevDetails.some(product => product.post_id === id);

      if (productExists) {
        return prevDetails.map(product => {
          if (product.post_id === id) {
            return {
              ...product,
              is_liked: newIsLiked,
              likes: newIsLiked
                ? [
                    ...(product.likes || []),
                    {
                      concat: user.photo_url,
                      id: Date.now(),
                      post_id: id,
                      telegram_link: `https://t.me/${user.username}`,
                      user_id: user.id,
                      username: user.username,
                    },
                  ]
                : (product.likes || []).filter(like => like.user_id !== user.id),
            };
          }
          return product;
        });
      }

      return [
        ...prevDetails,
        {
          comments: [],
          is_liked: newIsLiked,
          is_shared: false,
          likes: newIsLiked
            ? [
                {
                  concat: user.photo_url,
                  id: Date.now(),
                  post_id: id,
                  telegram_link: `https://t.me/${user.username}`,
                  user_id: user.id,
                  username: user.username,
                },
              ]
            : [],
          post_id: id,
          shares: [],
        },
      ];
    });
    try {
      await setLike(id);
    } catch (error) {
      console.error('Error occurred while setting like:', error);
    } finally {
      sendGTMEvent(GA4_KEY.set_like(id, !isLiked));
    }
  };

  const handleCommentClick = () => {
    telegram.HapticFeedback.impactOccurred('light');
    setShowComments(!showComments);
    sendGTMEvent(GA4_KEY.show_comments(product.id));
  };

  const handleShare = async () => {
    telegram.HapticFeedback.impactOccurred('medium');

    setProductsDetails(prevDetails => {
      if (!Array.isArray(prevDetails)) return prevDetails;

      const productExists = prevDetails.some(productDetail => productDetail.post_id === product.id);

      if (productExists) {
        return prevDetails.map(productDetail => {
          if (productDetail.post_id === product.id) {
            return {
              ...productDetail,
              is_shared: true,
              shares: [
                ...(productDetail.shares || []),
                {
                  concat: user.photo_url,
                  id: Date.now(),
                  post_id: product.id,
                  telegram_link: `https://t.me/${user.username}`,
                  user_id: user.id,
                  username: user.username,
                },
              ],
            };
          }
          return productDetail;
        });
      }

      return [
        ...prevDetails,
        {
          comments: [],
          is_liked: isLiked,
          is_shared: true,
          likes: likesCount,
          post_id: product.id,
          shares: [
            {
              concat: user.photo_url,
              id: Date.now(),
              post_id: product.id,
              telegram_link: `https://t.me/${user.username}`,
              user_id: user.id,
              username: user.username,
            },
          ],
        },
      ];
    });

    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(
      `${mini_app_url}?startapp=products_${product.id}`,
    )}&parse_mode=Markdown&text=${encodeURIComponent(`'${product.title}'`)}`;
    window.open(shareUrl, '_blank');

    try {
      await sharePost(product.id);
    } catch (error) {
      console.error('Error occurred while sharing post:', error);
    } finally {
      sendGTMEvent(GA4_KEY.share_post(product.id));
    }
  };

  return (
    <div
      id='product-modal'
      className={styles['product-modal']}
    >
      {showFullScreenPDF ? (
        <PDFViewer
          pdfUrl={product?.product}
          onClose={() => setShowFullScreenPDF(false)}
          toolbar={true}
          fullScreen={true}
        />
      ) : (
        <div className={styles['product-modal-content']}>
          <CachedImage
            src={product?.image ? product.image : `assets/why_beauty.webp`}
            alt={product?.title}
            className={`${styles['product-image']} ${'enabled' in product && !product?.enabled ? styles.disabled : ''}`}
          />
          <div className={`${styles['product-info']} ${showComments ? styles['product-comments-info'] : ''}`}>
            <div className={styles['product-stats']}>
              <div
                className={styles['stat-item']}
                onClick={() => handleSetLike(product.id)}
              >
                <i
                  className={`fas fa-heart ${styles['stat-icon']} ${isLiked ? styles['liked'] : ''}`}
                  id='like-button'
                ></i>
                <span id='product-likes'>{likesCount}</span>
              </div>
              <div
                className={styles['stat-item']}
                onClick={handleCommentClick}
              >
                <i
                  className={`fas fa-comment ${styles['stat-icon']}`}
                  id='comment-button'
                ></i>
                <span id='product-reviews'>{productDetails?.comments?.length || 0}</span>
              </div>
              <div
                className={styles['stat-item']}
                onClick={handleShare}
              >
                <i
                  className={`fas fa-paper-plane ${styles['stat-icon']}`}
                  id='share-button'
                ></i>
                <span id='product-shares'>{sharesCount}</span>
              </div>
              {likesCount > 0 && (
                <div className={styles['also-liked']}>
                  <span>Понравилось...</span>
                  <div className={styles['also-liked-avatars']}>
                    {isLiked && (
                      <CachedImage
                        key='current-user'
                        src={user.photo_url}
                        alt={user.name}
                        className={styles['also-liked-avatar']}
                      />
                    )}
                    {productDetails?.likes
                      ?.filter(like => like.user_id !== user.id)
                      .slice(0, isLiked ? 2 : 3)
                      .map(like => (
                        <CachedImage
                          key={like.id}
                          src={like.concat}
                          alt={like.username}
                          className={styles['also-liked-avatar']}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
            {showComments ? (
              <Comments
                comments={productDetails?.comments || []}
                onClose={() => setShowComments(false)}
              />
            ) : (
              <>
                {!product?.free && !('price' in product) && subscription_button && (
                  <div className={styles['premium-icon-wrapper']}>
                    <svg
                      width='14'
                      height='15'
                      viewBox='0 0 14 15'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M11.6146 11.1042L11.4835 11.3341C11.1023 12.0018 10.3903 12.4172 9.62189 12.4172H4.37796C3.60951 12.4172 2.89553 12.0018 2.51636 11.3341L2.38525 11.1042H11.6146Z'
                        fill='white'
                      />
                      <path
                        d='M11.6106 5.06961C11.2254 4.91631 10.7917 5.01112 10.5053 5.31162L9.64208 6.21521L7.95998 3.11121C7.78452 2.78649 7.44567 2.5848 7.07657 2.58279C7.07456 2.58279 7.07456 2.58279 7.07456 2.58279C6.70546 2.58279 6.36663 2.78448 6.18913 3.1092L4.43241 6.32616L3.49859 5.33184C3.21421 5.02931 2.78057 4.93249 2.39332 5.08579C2.00607 5.23908 1.75598 5.60613 1.75598 6.02363V9.89409C1.75598 10.1159 1.93549 10.2975 2.15937 10.2975H11.8405C12.0624 10.2975 12.2439 10.1159 12.2439 9.89409V6.00546C12.2439 5.592 11.9938 5.22491 11.6106 5.06961ZM7.40283 8.0895C7.40283 8.31226 7.22222 8.49289 6.99944 8.49289C6.77668 8.49289 6.59605 8.31228 6.59605 8.0895V7.49999C6.59605 7.27723 6.77666 7.0966 6.99944 7.0966C7.22219 7.0966 7.40283 7.27721 7.40283 7.49999V8.0895Z'
                        fill='white'
                      />
                    </svg>
                    <span>Доступно по подписке</span>
                  </div>
                )}
                {'enabled' in product && !product?.enabled && (
                  <div className={styles['premium-icon-wrapper']}>
                    <span>Сейчас недоступно</span>
                  </div>
                )}
                <h2
                  id='product-title'
                  className={styles['product-modal-title']}
                >
                  {product.title}
                </h2>

                <p
                  id='product-description'
                  className={styles['product-modal-description']}
                >
                  {product?.description}
                </p>
              </>
            )}
          </div>

          {!showComments && (
            <div className={styles['product-buy-wrapper']}>
              {('price' in product || product?.product) && (
                <button
                  className={styles['product-modal-buy']}
                  onClick={handleBuy}
                >
                  <span className={styles['buy-text']}>
                    {!('price' in product) &&
                      (product?.free || !subscription_button
                        ? 'Просмотреть'
                        : premium
                        ? 'Просмотреть'
                        : 'Получить доступ')}
                    {'price' in product && (product?.enabled ? product?.price : 'Узнать подробнее')}
                  </span>
                </button>
              )}

              {!('price' in product) && (
                <button
                  className={styles['product-modal-chat']}
                  onClick={() => {
                    const isTdesktop = telegram.platform === 'tdesktop';

                    telegram.setHeaderColor('#1D2733');
                    telegram.HapticFeedback.impactOccurred('light');
                    startConversation(`Привет! Расскажи мне про "${product?.title}"`);
                    if (!telegram?.initDataUnsafe?.query_id) {
                      telegram.openTelegramLink(bot_url);
                    }
                    document.querySelector('.App').classList.add('fadeOut');
                    telegram.HapticFeedback.impactOccurred('light');
                    sendGTMEvent(GA4_KEY.open_ai_chat);
                    if (isTdesktop) {
                      setTimeout(() => {
                        telegram.close();
                      }, 350);
                    } else {
                      telegram.close();
                    }
                  }}
                >
                  <span className={styles['buy-text']}>Задать вопрос в чате</span>
                </button>
              )}
            </div>
          )}
          {showComments && (
            <div className={styles['product-сomments-wrapper']}>
              <button
                className={styles['product-modal-buy']}
                onClick={() => {
                  telegram.HapticFeedback.impactOccurred('light');
                  setShowCommentForm(true);
                }}
              >
                <span className={styles['buy-text']}>Оставить комментарий</span>
              </button>
            </div>
          )}
          {showCommentForm && (
            <CommentForm
              postId={product.id}
              setProductsDetails={setProductsDetails}
              onClose={() => setShowCommentForm(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Product;
